import {
  BarChart,
  CurrencyYuan,
  DataObject,
  Dataset,
  Groups,
  Home,
  ManageAccounts,
  RunningWithErrors,
} from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai/index";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCfgPerm } from "@/auth/hooks";
import { SidebarMenuItemType } from "@/components/sidebar/sidebar-menu-item-type";
import { isOwner } from "@/lib/auth-predicates";
import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";
import { SysDomainRegularDto } from "@/shared/api";
import { appCurrentRoom, meStore } from "@/store/store";

const useSidebarMenuItems = ({ domains }: { domains: SysDomainRegularDto[] }) => {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);
  const navigate = useNavigate();
  const [selectedRoom] = useAtom(appCurrentRoom);

  const chatsPerms = useCfgPerm({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.REPORT_CHAT,
  });

  const rulesPerm = useCfgPerm({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.REPORT_RULE,
  });

  const roomTablesPerms = useCfgPerm({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.ROOM_TABLES_CACHE_ROOM_TABLES,
  });

  const selectedRoomConfigs = useMemo(() => {
    return selectedRoom?.config_names.filter((it) => it !== ROOM_CONFIGS_NAMES.ACCOUNT) ?? [];
  }, [selectedRoom]);

  const accountIsPresent = useMemo(
    () => selectedRoom?.config_names.some((it) => it === ROOM_CONFIGS_NAMES.ACCOUNT) ?? false,
    [selectedRoom]
  );

  const statAnalyticsIsPresent = useMemo(
    () => (me?.stat_visualization?.length ?? 0) > 0,
    [me]
  );

  const serviceDomain = useMemo(() => {
    return domains.find((it) => it.name === ROOM_NAMES.SERVICES);
  }, [domains]);

  const serviceConfigs = useMemo(() => {
    const resultConfigs = [];

    if (chatsPerms.read || rulesPerm.read) {
      resultConfigs.push(ROOM_CONFIGS_NAMES.PLAYERS_REPORTER);
    }

    if (roomTablesPerms.read) {
      resultConfigs.push(ROOM_CONFIGS_NAMES.TABLE_CACHE);
    }

    const configNames =
      serviceDomain?.config_names
        ?.filter(
          (it) =>
            it !== ROOM_CONFIGS_NAMES.REPORT_CHAT && it !== ROOM_CONFIGS_NAMES.REPORT_RULE
        )
        ?.filter(
          (it) =>
            it !== ROOM_CONFIGS_NAMES.ROOM_TABLES_CACHE_ROOM_TABLES &&
            it !== ROOM_CONFIGS_NAMES.ROOM_TABLES_CACHE_ROOM_INFO
        ) ?? [];

    return [...configNames, ...resultConfigs];
  }, [chatsPerms, roomTablesPerms, rulesPerm, serviceDomain]);

  //AICON
  /*const aiconDomain = useMemo(() => {
    return domains.find((it) => it.name === ROOM_NAMES.AICON);
  }, [domains]);

  const aiconConfigs = useMemo(() => {
    return aiconDomain?.config_names ?? [];
  }, [domains]);*/

  return useMemo<SidebarMenuItemType[]>(
    () => [
      {
        name: t("home"),
        icon: <Home />,
        link: { to: "/" },
      },
      {
        name: t("accounts"),
        icon: <AccountCircleIcon />,
        link: {
          to: "/accounts",
          isDisabled: true,
        },
        onClick: () =>
          navigate({
            to: "/accounts/$room/$type",
            params: { room: selectedRoom?.name, type: "regular" },
          }),
        isHiddenAccessor: () => !accountIsPresent,
      },
      {
        name: t("roomSettings"),
        icon: <DataObject />,
        link: { to: "/room-config" },
        subItemsAccessor: () =>
          selectedRoomConfigs.map((cfg) => ({
            name: cfg,
            menuItemText: cfg === ROOM_CONFIGS_NAMES.CMS_AGGREGATOR ? "Clubs Cms" : undefined,
            link: {
              to: "/room-config/$room/$config-name",
              params: { room: selectedRoom?.name, "config-name": cfg },
            },
          })),
        isHiddenAccessor: () => selectedRoomConfigs.length <= 0,
      },
      {
        name: t("services"),
        icon: <SettingsIcon />,
        link: { to: "/service-config" },
        subItemsAccessor: () =>
          serviceConfigs.map((cfg) => {
            return {
              name: t(`servicesMenu.${cfg}`, cfg),
              link: {
                to: "/service-config/$config-name",
                params: {
                  "config-name": cfg,
                },
              },
            };
          }) ?? [],
        isHiddenAccessor: () => !serviceDomain || serviceDomain.config_names.length <= 0,
      },
      /*{
        name: t("Aicon"),
        icon: <Psychology />,
        link: { to: "/aicon-config" },
        subItemsAccessor: () =>
          aiconConfigs.map((cfg) => {
            return {
              name: t(`aiconMenu.${cfg}`, cfg),
              link: {
                to: "/aicon-config/$config-name",
                params: {
                  "config-name": cfg,
                },
              },
            };
          }) ?? [],
        isHiddenAccessor: () => !serviceDomain || serviceDomain.config_names.length <= 0,
      },*/
      {
        name: t("userManagement"),
        link: {
          to: "/users",
        },
        icon: <ManageAccounts />,
        isHiddenAccessor: () => !isOwner(me),
      },
      {
        name: t("authGroups"),
        link: {
          to: "/auth-groups",
        },
        icon: <Groups />,
        isHiddenAccessor: () => !isOwner(me),
      },
      {
        name: t("instances"),
        link: {
          to: "/instance",
        },
        icon: <DashboardIcon />,
        isHiddenAccessor: () => !isOwner(me),
      },
      {
        name: t("roomManagement"),
        link: {
          to: "/room-management",
        },
        icon: <Dataset />,
        isHiddenAccessor: () => !isOwner(me),
      },
      {
        name: t("Statanalytics"),
        link: {
          to: "/statanalytics",
        },
        icon: <BarChart />,
      },
      {
        name: t("mttAnalytics"),
        link: {
          to: "/mtt-analytics",
        },
        icon: <CurrencyYuan />,
      },
      {
        name: t("hhErrorsJournal"),
        link: {
          to: "/hh-errors-journal",
        },
        icon: <RunningWithErrors />,
        isHiddenAccessor: () => !isOwner(me),
      },
    ],
    [
      accountIsPresent,
      me,
      navigate,
      selectedRoom,
      selectedRoomConfigs,
      serviceConfigs,
      serviceDomain,
      t,
    ]
  );
};

export default useSidebarMenuItems;
